/**
 * Timeline — the archive laid out along a real axis, scrolled sideways.
 *
 * This was a vertical list of entries with a small scrubber above it, which
 * had the axis in the wrong place: the list did the reading and the axis only
 * reported where you were in it, so the thing that makes a timeline a timeline
 * — that the distance between two moments is how far apart they happened —
 * was reduced to a 76px strip nobody could touch. Time runs left to right
 * here, a year is always the same number of pixels wide, and the entries stand
 * on it where they belong. A decade the family went unphotographed is a gap
 * you have to travel across.
 *
 * Photographs are on the axis rather than behind a click: each stretch of
 * years carries a stack of them, filled as it comes into view, and clicking
 * one opens it. The label under a stack narrows the filter to those years and
 * hands over to the grid — the axis is for finding the moment, the grid is for
 * working in it.
 *
 * Stories are first-class here, and that is the other reason this is not the
 * date buckets. `/timeline` derives its buckets from photographs, so a period
 * annotation covering years that produced no surviving photograph would never
 * appear — and those are exactly the years somebody most needs to write down.
 * The journal is fetched alongside and the orphans are spliced back in as
 * entries of their own, with nothing above them and a line saying so.
 */

import { h, replace, clear } from "../dom";
import { api, assetImageUrl } from "../api";
import { store } from "../state";
import { empty, failure, fingerprint, loading, seg } from "../ui";
import { count, formatDate, personName } from "../format";
import { openAsset } from "../lightbox";
import type { Annotation, TimelineEntry, TimelineStory } from "../types";
import type { RefreshOptions, View } from "./view";

type Grain = "year" | "decade";


const span = (grain: Grain): number => (grain === "decade" ? 10 : 1);

const bucketOf = (iso: string, grain: Grain): string => {
  const y = iso.slice(0, 4);
  return grain === "decade" ? `${y.slice(0, 3)}0` : y;
};

const entryLabel = (bucket: string, grain: Grain): string =>
  grain === "decade" ? formatDate(`${bucket}-01-01`, "decade") : bucket;

/**
 * How long a period story covers, in its own words.
 *
 * A story about "the fifties" is one story about ten years, not ten stories.
 * The label carries the span so the entry it hangs off does not have to.
 */
export function periodLabel(an: Annotation): string {
  const p = an.period_precision;
  const from = formatDate(an.period_start, p, { short: true });
  const to = an.period_end ? formatDate(an.period_end, p, { short: true }) : null;
  return !to || to === from ? from : `${from} – ${to}`;
}

/**
 * Timeline entries, plus one synthesised entry per period story that no
 * photograph put on the axis. Ascending, because this is a life read forwards.
 *
 * A story is placed once, at the earliest bucket it touches. `/timeline`
 * repeats an overlapping period story in every bucket it spans — correct for
 * the query, unreadable on a page, because a decade at year grain would print
 * the same paragraph ten times. The span is shown in the story's own label
 * instead.
 */
export function weave(
  entries: TimelineEntry[],
  journal: Annotation[],
  grain: Grain,
): TimelineEntry[] {
  const step = span(grain);
  const byBucket = new Map<string, TimelineEntry>();
  for (const e of entries) byBucket.set(e.bucket, { ...e, stories: [...e.stories] });

  for (const an of journal) {
    if (an.subject_kind !== "period") continue;
    const start = an.period_start ?? an.period_end;
    if (!start) continue;
    const bucket = bucketOf(start, grain);
    const story: TimelineStory = {
      id: an.id,
      kind: an.kind,
      body: an.body,
      attributedTo: an.attributed_to,
    };
    const at = byBucket.get(bucket);
    if (at) {
      if (!at.stories.some((s) => s.id === an.id)) at.stories.push(story);
    } else {
      byBucket.set(bucket, {
        bucket,
        label: bucket,
        from: `${bucket}-01-01`,
        to: `${Number(bucket) + step - 1}-12-31`,
        assetCount: 0,
        videoCount: 0,
        coverHash: null,
        people: [],
        stories: [story],
      });
    }
  }

  const ordered = [...byBucket.values()].sort((a, b) => Number(a.bucket) - Number(b.bucket));
  const seen = new Set<string>();
  const out: TimelineEntry[] = [];
  for (const e of ordered) {
    const stories = e.stories.filter((s) => !seen.has(s.id));
    for (const s of stories) seen.add(s.id);
    // A bucket that only existed to carry a story somebody else is already
    // carrying is not an entry.
    if (!e.assetCount && !stories.length) continue;
    out.push({ ...e, stories });
  }
  return out;
}

/**
 * Zoom, in pixels per year.
 *
 * The axis is a true time axis: a year always occupies the same width, so a
 * decade nobody photographed is a visible gap rather than two adjacent rows.
 * That is the whole reason this view exists and not the date buckets, and it
 * only works if the mapping from year to x is linear and never adjusted to
 * make the content fit.
 */
const ZOOMS = [
  { label: "Decades", px: 26 },
  { label: "Years", px: 150 },
  { label: "Close", px: 420 },
] as const;

/** Thumbnails fetched per bucket. More than this and a stack stops reading. */
const SHOTS = 8;
const AXIS_PAD = 140;
/**
 * Blank axis before the first year.
 *
 * A year label is centred on its tick, so the earliest one was cut in half by
 * the left edge — "1961" rendered as "61", which is not a wrong label, it is a
 * different year. This also gives the oldest stack somewhere to sit that is
 * not flush against the frame.
 */
const AXIS_LEAD = 30;
const SHOT_W = 48;
const SHOT_GAP = 4;

/**
 * How often to print a year on the ruler, given the zoom.
 *
 * The finest interval whose labels still have room to sit side by side. This
 * was written the wrong way round — labelling *less* often the further in you
 * zoomed — so close zoom put a single number on a five-year span while decade
 * zoom tried to print one every 26px. A year in tabular figures is about 40px
 * wide, so the rule is only ever "the closest spacing that still fits".
 */
export function labelInterval(px: number): number {
  const LABEL_W = 44;
  return [1, 2, 5, 10, 25, 50, 100].find((n) => n * px >= LABEL_W) ?? 100;
}

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let observer: IntersectionObserver | null = null;
  let detach: Array<() => void> = [];

  function storyEl(
    s: TimelineStory,
    nameOf: (id: string) => string,
    covers: (id: string) => string | null,
  ): HTMLElement {
    const period = covers(s.id);
    return h(
      "div",
      { class: "story" },
      h(
        "div",
        { class: "meta" },
        h("span", { class: "kind", text: s.kind }),
        period ? h("span", { class: "covers", text: period }) : null,
        s.attributedTo ? h("span", { text: `as told by ${nameOf(s.attributedTo)}` }) : null,
      ),
      h("p", { text: s.body ?? "" }),
    );
  }

  /**
   * One stretch of years, standing on the axis at the year it happened.
   *
   * Absolutely positioned rather than laid out in a row, because the gaps
   * between them carry meaning — the distance from one entry to the next is
   * how long the family went unphotographed.
   */
  function entryEl(
    e: TimelineEntry,
    grain: Grain,
    px: number,
    x: number,
    nameOf: (id: string) => string,
    covers: (id: string) => string | null,
  ): HTMLElement {
    const shots = h("div", { class: "tlshots" });
    const step = span(grain);

    // The width this stretch of years actually occupies on the axis. The stack
    // is not allowed past it: a run of eight thumbnails is 400px, which at
    // year zoom is three years of axis, and letting it spill would put 1961's
    // photographs over 1964 and make the distances — the only thing this view
    // claims to show — a lie.
    const bandW = step * px - SHOT_GAP;

    const el = h(
      "div",
      {
        class: e.assetCount === 0 ? "tlentry storyonly" : "tlentry",
        "data-bucket": e.bucket,
        style: `left:${x}px;width:${Math.max(SHOT_W, bandW)}px`,
      },
      h("div", { class: "tltold", hidden: true }),
      shots,
      h(
        "button",
        {
          class: "tlwhen",
          // The label narrows to these years and jumps to the grid. The axis
          // is for finding the moment; the grid is for working in it.
          title: `Show only ${entryLabel(e.bucket, grain)}`,
          onclick: () => {
            store.setFilter({ from: e.from, to: e.to });
            store.go("cards");
          },
        },
        h("span", { class: "lab", text: entryLabel(e.bucket, grain) }),
        h("span", {
          class: "n",
          text:
            e.assetCount === 0
              ? `${e.stories.length} told`
              : `${e.assetCount}${e.videoCount ? ` · ${e.videoCount} film` : ""}`,
        }),
      ),
    );

    const told = el.querySelector<HTMLElement>(".tltold")!;
    if (e.stories.length) {
      told.hidden = false;
      for (const st of e.stories) told.appendChild(storyEl(st, nameOf, covers));
    }

    if (e.assetCount === 0) {
      shots.appendChild(
        h("div", { class: "tlnophoto", text: "nothing survives from these years" }),
      );
      return el;
    }

    // Filled when it scrolls into view. A century at close zoom is thousands
    // of pixels of axis and a request per bucket would be a hundred requests
    // for photographs nobody has looked at yet.
    let loaded = false;
    (el as HTMLElement & { fill?: () => void }).fill = () => {
      if (loaded) return;
      loaded = true;
      const mine = generation;
      void api
        .cards(store.libraryId, { ...store.filter, from: e.from, to: e.to }, { limit: SHOTS })
        .then((slice) => {
          if (mine !== generation) return;
          clear(shots);
          const rest = e.assetCount - slice.items.length;
          const chips = slice.items.length + (rest > 0 ? 1 : 0);

          // How far apart to place them. Side by side where the band is wide
          // enough, overlapping like a fanned deck where it is not — which is
          // both what a stack of prints looks like and an honest picture of
          // "more happened here than there is room to show". Never wider than
          // the band, so the axis stays true at every zoom.
          const room = Math.max(SHOT_W, bandW);
          const pitch =
            chips > 1
              ? Math.min(SHOT_W + SHOT_GAP, (room - SHOT_W) / (chips - 1))
              : SHOT_W + SHOT_GAP;
          const overlapped = pitch < SHOT_W + SHOT_GAP;
          shots.classList.toggle("fanned", overlapped);

          slice.items.forEach((a, i) => {
            const src = assetImageUrl(store.libraryId, a);
            if (!src) return;
            shots.appendChild(
              h(
                "button",
                {
                  class: "tlshot",
                  // Later ones on top, so the fan reads left to right and the
                  // hover lift is never behind its neighbour.
                  style: `left:${i * pitch}px;z-index:${i + 1}`,
                  title: `${a.caption ?? a.fileName} — ${formatDate(a.capturedAt, a.capturedPrecision, { short: true })}`,
                  onclick: () => openAsset(a),
                },
                h("img", { src, alt: a.caption ?? a.fileName, loading: "lazy" }),
              ),
            );
          });

          if (rest > 0) {
            shots.appendChild(
              h("button", {
                class: "tlshot more",
                style: `left:${slice.items.length * pitch}px;z-index:${chips + 1}`,
                text: `+${rest}`,
                title: `Show all ${e.assetCount} from ${entryLabel(e.bucket, grain)}`,
                onclick: () => {
                  store.setFilter({ from: e.from, to: e.to });
                  store.go("cards");
                },
              }),
            );
          }
        })
        .catch(() => {
          loaded = false;
          replace(shots, h("div", { class: "tlnophoto", text: "could not load these" }));
        });
    };
    return el;
  }

  /**
   * The ruler under the axis.
   *
   * Every year gets a tick and every decade a labelled one, drawn across the
   * whole span rather than only where photographs exist — the empty stretches
   * are the point. Decade zoom labels centuries instead, because a tick per
   * year at 16px is a grey band.
   */
  function ruler(minYear: number, maxYear: number, px: number): HTMLElement {
    const rule = h("div", { class: "tlruler" });
    const labelEvery = labelInterval(px);
    for (let y = minYear; y <= maxYear; y++) {
      const at = AXIS_LEAD + (y - minYear) * px;
      const major = y % labelEvery === 0;
      rule.appendChild(
        h("div", { class: major ? "tick major" : "tick", style: `left:${at}px` }),
      );
      if (major) {
        rule.appendChild(h("span", { class: "tlyear", style: `left:${at}px`, text: String(y) }));
      }
    }
    return rule;
  }

  /** What the axis was last drawn from, so an unchanged live update does nothing. */
  let shownSig: string | null = null;

  /** Tear down the drawn axis's observer and listeners, before drawing another. */
  const teardown = (): void => {
    observer?.disconnect();
    observer = null;
    for (const off of detach) off();
    detach = [];
  };

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    const mine = ++generation;
    // A quiet update leaves the axis working until it knows it must redraw:
    // tearing down first and then finding nothing changed would leave a rail
    // that no longer scrolls or fills.
    const quiet = Boolean(opts.quiet && shownSig !== null);
    if (!quiet) {
      shownSig = null;
      teardown();
    }

    const grain: Grain = store.timelineGrain;
    const zoomIndex = store.timelineZoom;
    const px = ZOOMS[zoomIndex]!.px;

    const bar = h(
      "div",
      { class: "tlbar" },
      seg(
        "zoom",
        ZOOMS.map((z, i) => [String(i), z.label] as [string, string]),
        String(zoomIndex),
        (v) => {
          store.timelineZoom = Number(v);
          // Decade buckets at close zoom are ten-year-wide blocks with one
          // label; year buckets at decade zoom are unreadably dense. The
          // grain follows the zoom rather than being a second control asking
          // about the same thing.
          store.timelineGrain = Number(v) === 0 ? "decade" : "year";
          void refresh();
        },
      ),
    );
    if (!quiet) replace(pane, bar, loading("the timeline"));

    let entries: TimelineEntry[];
    let journal: Annotation[];
    try {
      [entries, journal] = await Promise.all([
        api.timeline(store.libraryId, store.filter, grain),
        api.journal(store.libraryId),
      ]);
    } catch (e) {
      if (mine !== generation || quiet) return;
      replace(pane, bar, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    const sig = fingerprint([grain, zoomIndex, entries, journal]);
    if (quiet && sig === shownSig) return;

    // Period stories are about the years themselves, not about a person or an
    // album, so a person filter should not silently drop them. They are only
    // suppressed when the reader has asked for a date range.
    const relevant = store.filter.from || store.filter.to ? [] : journal;
    const woven = weave(entries, relevant, grain);

    if (!woven.length) {
      teardown();
      shownSig = sig;
      replace(
        pane,
        bar,
        empty({
          icon: "timeline",
          title: store.narrowed ? "Nothing on the timeline matches" : "Nothing to lay out yet",
          body: [
            store.narrowed
              ? "No photographs and no stories fall inside this narrowing."
              : "As photographs are dated they take their place here. Stories written about a stretch of years appear too, even for years that produced no photograph at all.",
          ],
          actions: store.narrowed
            ? [h("button", { class: "btn primary", text: "Clear the filter", onclick: () => store.clearFilter() })]
            : [],
        }),
      );
      return;
    }

    const names = new Map<string, string>();
    for (const e of woven) for (const p of e.people) names.set(p.identityId, personName(p.name));
    try {
      for (const p of await api.people(store.libraryId)) names.set(p.identityId, personName(p.name));
    } catch {
      /* names are a nicety here; ids would still render */
    }
    if (mine !== generation) return;
    const nameOf = (id: string): string => names.get(id) ?? "someone";

    const spans = new Map<string, string>();
    for (const an of journal) {
      if (an.subject_kind === "period" && (an.period_start || an.period_end)) {
        spans.set(an.id, periodLabel(an));
      }
    }
    const covers = (id: string): string | null => spans.get(id) ?? null;

    const years = woven.map((e) => Number(e.bucket));
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years) + span(grain) - 1;
    const width = AXIS_LEAD + (maxYear - minYear + 1) * px + AXIS_PAD;

    const track = h("div", { class: "tltrack", style: `width:${width}px` });
    track.appendChild(ruler(minYear, maxYear, px));
    track.appendChild(h("div", { class: "tlline" }));

    const els = woven.map((e) => {
      const x = AXIS_LEAD + (Number(e.bucket) - minYear) * px;
      const el = entryEl(e, grain, px, x, nameOf, covers);
      // Kept on the element so the first screenful can be filled from
      // arithmetic rather than from a layout callback — see below.
      el.dataset.x = String(x);
      return el;
    });
    for (const el of els) track.appendChild(el);

    // A rail tall enough for stories when there are stories, and no taller.
    // Reserving the room unconditionally left two thirds of the view as empty
    // space above a strip of photographs, which reads as a rendering fault
    // rather than as a page with room to grow.
    const anyStories = woven.some((e) => e.stories.length > 0);
    const rail = h("div", {
      class: anyStories ? "tlrail" : "tlrail bare",
      tabindex: "0",
      "aria-label": "The archive in order",
    });
    rail.appendChild(track);

    bar.appendChild(h("div", { class: "grow" }));
    bar.appendChild(
      h("span", {
        class: "stat",
        text: [
          minYear === maxYear ? String(minYear) : `${minYear}–${maxYear}`,
          count(woven.length, grain === "decade" ? "decade" : "year"),
          count(
            woven.reduce((n, e) => n + e.stories.length, 0),
            "story",
            "stories",
          ),
        ].join(" · "),
      }),
    );

    // From here to the end is synchronous, so the old axis is never torn down
    // without the new one taking its place.
    const heldLeft = quiet ? pane.querySelector<HTMLElement>(".tlrail")?.scrollLeft : undefined;
    teardown();
    shownSig = sig;
    replace(pane, bar, rail);

    // --- moving along it ---------------------------------------------------

    // A vertical wheel is what a mouse and a trackpad both produce, and this
    // axis only goes sideways. A horizontal gesture is passed through so a
    // trackpad swipe still works normally.
    const onWheel = (ev: WheelEvent): void => {
      if (Math.abs(ev.deltaY) <= Math.abs(ev.deltaX)) return;
      ev.preventDefault();
      rail.scrollLeft += ev.deltaY;
    };
    rail.addEventListener("wheel", onWheel, { passive: false });
    detach.push(() => rail.removeEventListener("wheel", onWheel));

    // Drag to pan, because a long axis with no visible scrollbar on a
    // trackpad is a thing people try to grab.
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    const down = (ev: PointerEvent): void => {
      // Not on a photograph or a label: those are for clicking.
      if ((ev.target as HTMLElement).closest("button")) return;
      dragging = true;
      startX = ev.clientX;
      startScroll = rail.scrollLeft;
      rail.classList.add("dragging");
    };
    const move = (ev: PointerEvent): void => {
      if (!dragging) return;
      rail.scrollLeft = startScroll - (ev.clientX - startX);
    };
    const up = (): void => {
      dragging = false;
      rail.classList.remove("dragging");
    };
    rail.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    detach.push(() => {
      rail.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    });

    // Arrow keys, Home and End, because an axis is a thing you should be able
    // to walk without a mouse.
    const onKey = (ev: KeyboardEvent): void => {
      const page = rail.clientWidth * 0.8;
      const step =
        ev.key === "ArrowLeft" ? -px * 2
        : ev.key === "ArrowRight" ? px * 2
        : ev.key === "PageUp" ? -page
        : ev.key === "PageDown" ? page
        : null;
      if (step !== null) {
        ev.preventDefault();
        rail.scrollLeft += step;
        return;
      }
      if (ev.key === "Home") {
        ev.preventDefault();
        rail.scrollLeft = 0;
      } else if (ev.key === "End") {
        ev.preventDefault();
        rail.scrollLeft = rail.scrollWidth;
      }
    };
    rail.addEventListener("keydown", onKey);
    detach.push(() => rail.removeEventListener("keydown", onKey));

    // Opens at the most recent end. An archive is added to at the present, so
    // the newest years are the ones somebody has just been working on, and the
    // far past is a scroll away rather than the other way round.
    //
    // Measured from the last entry rather than from the end of the track. The
    // track carries blank axis past its final year — a stretch of time with
    // nothing in it yet — and scrolling to the track's edge parked that blank
    // stretch in the window with the photographs off to the left. On a
    // single-year archive it pushed the only stack there is clean off screen.
    const lastX = els.length ? Number(els[els.length - 1]!.dataset.x ?? "0") : 0;
    const want = lastX + span(grain) * px + 40 - rail.clientWidth;
    rail.scrollLeft = Math.max(0, Math.min(want, rail.scrollWidth - rail.clientWidth));
    // A live update keeps the reader where they were rather than jumping back
    // to the newest end.
    if (heldLeft !== undefined) rail.scrollLeft = heldLeft;

    /**
     * Fill the first screenful now, from arithmetic.
     *
     * Every entry's x is known, so what is on screen is a subtraction and does
     * not need the browser to measure anything. Waiting for the observer meant
     * an axis with no photographs on it until a callback arrived — and in a
     * background or hidden tab that callback never arrives at all, so the view
     * simply stayed empty. The observer is for what scrolls in later, which is
     * the thing it is actually good at.
     */
    const REACH = 600;
    const from = rail.scrollLeft - REACH;
    const to = rail.scrollLeft + rail.clientWidth + REACH;
    for (const el of els) {
      const x = Number(el.dataset.x ?? "0");
      if (x >= from && x <= to) (el as HTMLElement & { fill?: () => void }).fill?.();
    }

    // And what is about to be.
    observer = new IntersectionObserver(
      (ins) => {
        for (const i of ins) {
          if (!i.isIntersecting) continue;
          (i.target as HTMLElement & { fill?: () => void }).fill?.();
          observer?.unobserve(i.target);
        }
      },
      { root: rail, rootMargin: "0px 900px" },
    );
    for (const el of els) observer.observe(el);

    // A scroll is the other way work arrives, and it needs no layout callback
    // either — belt and braces for the case the observer is asleep.
    const onScroll = (): void => {
      const lo = rail.scrollLeft - REACH;
      const hi = rail.scrollLeft + rail.clientWidth + REACH;
      for (const el of els) {
        const x = Number(el.dataset.x ?? "0");
        if (x >= lo && x <= hi) (el as HTMLElement & { fill?: () => void }).fill?.();
      }
    };
    rail.addEventListener("scroll", onScroll, { passive: true });
    detach.push(() => rail.removeEventListener("scroll", onScroll));
  }

  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
      observer?.disconnect();
      for (const off of detach) off();
      detach = [];
    },
  };
}
